/*
 * The scan station's offline queue (6.9 backlog; server side: migration 0046,
 * mushroom-bank/lambda/idempotency.py).
 *
 * Not offlineQueue.js: that one carries whole records to /sync with
 * last-write-wins. This one carries individual station ACTIONS -- a plate
 * check, a reading, an observation -- each to its own endpoint, which are
 * not upserts: sent twice, they would be applied twice.
 *
 * So every write the station makes carries an Idempotency-Key. When a write
 * gets NO answer -- the lab's network is down -- it is kept here, with that
 * key, and replayed later. The server applies a key once and answers a repeat
 * with the first answer, so a replay after a request that DID arrive (only its
 * answer was lost) changes nothing.
 *
 * What the server REFUSES (a 4xx other than 426, which only means the app and
 * the server are mid-deploy on different versions) is not retried: it stays here, marked failed
 * with the server's words, until a person dismisses it. Storage is this
 * browser's localStorage -- a station queues a handful of actions, not a field
 * day of records; a browser that refuses storage has no queue, and the write
 * fails as it would have before.
 */
const STORAGE_KEY = 'pitriot.scan.queue';

export function newKey() {
  const rand = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `scan-${rand}`;
}

export function list() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function write(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    return true;
  } catch {
    return false;
  }
}

/** Keep a write for later. Returns false when this browser cannot store it. */
export function add(item) {
  return write([...list().filter((i) => i.key !== item.key),
    { ...item, queuedAt: item.queuedAt || new Date().toISOString(), failed: null }]);
}

export function dismiss(key) {
  write(list().filter((i) => i.key !== key));
}

/** True when the request got no answer at all (as opposed to a refusal). */
export function unreachable(error) {
  return !error?.response;
}

/** Send one write with its key. Resolves to the response; rejects as axios does. */
export function send(api, { path, body, key }) {
  return api.post(path, body, { headers: { 'Idempotency-Key': key } });
}

/**
 * Replay everything still waiting, oldest first. Stops at the first write
 * that gets no answer (still offline). Returns {sent, failed, waiting}.
 */
export async function flush(api) {
  let sent = 0;
  let failed = 0;
  for (const item of list()) {
    if (item.failed) { failed += 1; continue; }
    try {
      await send(api, item);
      dismiss(item.key);
      sent += 1;
    } catch (e) {
      // A 426 is a deploy in progress, not a refusal of the action: it stays
      // queued and goes on the next flush. The server refuses it before its
      // idempotency layer, so nothing was applied and the replay is safe.
      if (unreachable(e) || e.response?.status === 426) break;
      const message = e.response?.data?.error || `HTTP ${e.response.status}`;
      write(list().map((i) => (i.key === item.key ? { ...i, failed: message } : i)));
      failed += 1;
    }
  }
  const waiting = list().filter((i) => !i.failed).length;
  return { sent, failed, waiting };
}
